import React, { forwardRef, useCallback, useImperativeHandle, useState, useEffect, useMemo, useRef } from 'react';
import FilterListIcon from '@material-ui/icons/FilterList';
import DropdownMenuFilter from '../../../components/DropdownMenu/DropdownMenuFilter.component';
import { useStyles } from './ExaminerTreemapTable.styles';
import { useStyles as useTableStyles } from '../../styles';
import {
  ITaskGroup,
  Examiner,
  AssementSessionData,
  IExaminingTeamMemberWithFunctionId,
} from '../../../models/taskGroup.types';
import { AddSampleAssessmentGroupDialog } from '../Actions/AddSampleAssessmentGroupDialog.component';
import { PromptDetails } from '../../../models/promptsTypeModels.types';
import { Prompt, SubText } from '../../../components/Prompt/Prompt.component';
import AddKGZPrompt from '../Actions/AddKGZPrompt.component';
import UnassignExamTaskGroupCoordinatorDialog from '../Actions/UnassignExamTaskGroupCoordinatorDialog.component';
import DisableExaminer from '../Actions/DisableExaminer.component';
import EnableExaminer from '../Actions/EnableExaminer.component';
import { SampleAssessmentGroupChairmanAssignDialog } from '../Actions/SampleAssessmentGroupChairmanAssignDialog.component';
import FilterStateGroup from '../../../models/filterStateGroup.types';
import { SessionsParametersCardLimitPerExaminerDialog } from '../../Sessions/SessionsParametersDialog/SessionsParametersCardLimitPerExaminer/SessionsParametersCardLimitPerExaminerDialog';
import { ExaminerTreemapTableItem, ExaminerType, MessageModal, OpenFilterHandler } from '../ExaminerTreemap.types';
import { NoRecords } from '../../../components/NoRecords/NoRecords.component';
import TooltipOne from '../../../components/TooltipOne/TooltipOne.component';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import useSessionUser from '../../../hooks/useSessionUser';
import { ExaminerFunction } from '../../Assessment/Assessment.types';
import { DisableExaminingTeam } from '../Actions/DisableExaminingTeam.component';
import { BlockadeTypeId } from '../../../components/ExaminersStructure/ExaminersStructure.types';
import { NewMessageModal } from '../../../components/NewMessage/NewMessageModal.component';
import { useLayoutContext } from '../../../Context/Layout.context';
import { SelectableTable } from '../../../components/SelectableTable/SelectableTable.component';
import { fill } from '../../../utils/contrastSvgAttributes';
import { convertToDropdownFilterLabelItems } from '../../SampleAssessmentRepo/Helpers/AccountTypeRendering';
import { CircularProgress } from '@material-ui/core';
import ExaminerAssignNumber from '../../Examiner/ExaminerAssignNumber/ExaminerAssignNumber.component';
import { useExaminerTreemapTableRows } from './hooks/useExaminerTreemapTableRows';
import { useExaminerTreemapTableColumns } from './hooks/useExaminerTreemapTableColumns';
import { useExaminerTreemapApiActions } from './hooks/useExaminerTreemapApiActions';
import { findExaminersBasedOnFilter } from './examinerTreemapSearchUtils';

export interface ExaminerTreemapTableProps {
  data: ITaskGroup[];
  expandedItems: string[];
  handleExpandedItems: (id?: string) => void;
  filterTaskGroupsMenuItems: string[];
  filterFunctionsMenuItems: string[];
  firstNameSearchInput: string;
  lastNameSearchInput: string;
  examinerNumberSearchInput: string;
  handleSearchInput: (name: string, value: string) => void;
  reload: () => void;
  handleClickTaskGroupMenu: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  handleClickFunctionMenu: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  handleClickExamCommitteeMenu: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  checkedTaskGroupsMenuItems: string[];
  checkedFunctionsMenuItems: string[];
  checkedExamCommitteeMenuItems: string[];
  setCheckedTaskGroupsMenuItems: (items: string[]) => void;
  setCheckedFunctionsMenuItems: (items: string[]) => void;
  handleTaskGroupFilterChange: (item: string) => void;
  handleFunctionsFilterChange: (item: string) => void;
  handleExamCommitteeFilterChange: (item: string) => void;
  uncheckAllTaskGroups: () => void;
  uncheckAllFunctions: () => void;
  uncheckAllExamCommittee: () => void;
  uncheckAllFilters: () => void;
  onModalFunctionAction: (data: PromptDetails) => void;
  isAnyFiltersActive: boolean;
  filterState: FilterStateGroup[];
  handleCloseFilterItem: (item: string) => void;
  openAssignModal: (
    title: string,
    type: ExaminerType,
    isMultiChoice?: boolean,
    assessmentId?: string,
    examinerFunctionId?: string,
    secondaryId?: string,
  ) => void;
  isAnyInputActive: boolean;
  isLoader: boolean;
  isTableScrolled?: boolean;
  examinerMode?: boolean;
  isControlProcessActive: boolean;
  assessmentSessionId: string;
  assessmentSessionName: string;
}

export interface ShareExaminterTreeMapFunction {
  ReloadTreeMap: () => void;
  CloseMenu: () => void;
  selectedItemId: string;
  selectedSecondaryId: string | undefined;
}

const treemapItemsPagination = 100;
export const ExaminerTreemapTable = forwardRef<ShareExaminterTreeMapFunction | undefined, ExaminerTreemapTableProps>(
  (props, ref) => {
    const {
      examinerMode,
      expandedItems,
      handleCloseFilterItem,
      handleExpandedItems,
      onModalFunctionAction,
      openAssignModal,
      reload,
    } = props;

    const classes = useStyles();
    const tableClasses = useTableStyles();
    const [examiners, setExaminers] = useState<ITaskGroup[] | null>(null);
    const [openDisableExaminerPrompt, setOpenDisableExaminerPrompt] = useState<boolean>(false);
    const [openDisableExaminingTeamPrompt, setOpenDisableExaminingTeamPrompt] = useState<boolean>(false);
    const [openEnableExaminerPrompt, setOpenEnableExaminerPrompt] = useState<boolean>(false);
    const selectedItemId = useRef<string>('');
    const selectedAssessmentSessionId = useRef<string>('');
    const selectedTaskGroup = useRef<ITaskGroup>();
    const selectedExaminer = useRef<Examiner | undefined>();
    const examiningTeamMember = useRef<IExaminingTeamMemberWithFunctionId>();
    const selectedSecondaryId = useRef<string>('');
    const [menuAnchor, setMenuAnchor] = useState<SVGSVGElement>();
    const [anchorElTaskGroup, setAnchorElTaskGroup] = useState<SVGSVGElement | HTMLElement | null>(null);
    const [anchorElFunction, setAnchorElFunction] = useState<SVGSVGElement | HTMLElement | null>(null);
    const [anchorElExamCommittee, setAnchorElExamCommittee] = useState<SVGSVGElement | HTMLElement | null>(null);
    const [isAssignSampleAssessmentGroupChairmantDialogVisible, setIsAssignSampleAssessmentGroupChairmanDialogVisible] =
      useState<boolean>(false);
    const [openLimitCardPerExaminerDialog, setOpenLimitCardPerExaminerDialog] = useState<boolean>(false);
    const [openUnassignDialog, setOpenUnassignDialog] = useState<boolean>(false);
    const [isConfirmationPromptVisible, setIsConfirmationPromptVisible] = useState<boolean>(false);
    const [isSuccessPromptVisible, setIsSuccessPromptVisible] = useState<boolean>(false);
    const [isErrorPromptVisible, setIsErrorPromptVisible] = useState<boolean>(false);
    const [promptMainText, setPromptMainText] = useState<string>('');
    const [promptSubTexts, setPromptSubTexts] = useState<SubText[]>();

    const [isEmptyResult, setIsEmptyResult] = useState<boolean>(false);
    const [examinersFound, setExaminersFound] = useState<string[]>([]);
    const [isExaminingTeamChairmen, setIsExaminingTeamChairmen] = useState<boolean>(false);
    const { userAssessmentSessionInfo, isSessionLoaded, isTechAdmin } = useSessionUser();
    const examinerFunction = userAssessmentSessionInfo?.function;
    const [messageModal, setMessageModal] = useState<MessageModal>({
      open: false,
      type: 'standard',
    });

    const { updateStableMenuAnchor } = useLayoutContext();

    const [editExaminerNumberModalOpen, setEditExaminerNumberModalOpen] = useState<boolean>(false);

    const defaultFilter = useMemo(
      () => ({
        taskGroupName: '',
        firstName: props.firstNameSearchInput,
        lastName: props.lastNameSearchInput,
        examinerNumber: props.examinerNumberSearchInput,
        examCommittee: '',
      }),
      [props.examinerNumberSearchInput, props.firstNameSearchInput, props.lastNameSearchInput],
    );

    const hasAdminExaminersLineupsManagePermisson = usePermission(UserPermission.AdminExaminersLineupsManage);
    const hasAdminOkeExaminersLineupsManagePermission = usePermission(UserPermission.AdminOkeExaminersLineupsManage);
    const onOpenFilter: OpenFilterHandler = useCallback(
      (
        event: React.MouseEvent<SVGSVGElement | HTMLElement> | React.KeyboardEvent<SVGSVGElement | HTMLElement>,
        anchorElement: SVGSVGElement | HTMLElement | null,
        setAnchorElement: React.Dispatch<React.SetStateAction<SVGSVGElement | HTMLElement | null>>,
      ) => {
        if (!anchorElement) {
          const rect = event.currentTarget?.getBoundingClientRect();
          if (rect) {
            updateStableMenuAnchor(rect.left, rect.top);
          }
          setAnchorElement(event.currentTarget);
        } else {
          setAnchorElement(null);
        }
      },
      [updateStableMenuAnchor],
    );

    const useMenuHandler = (
      anchorElement: SVGSVGElement | HTMLElement | null,
      setAnchorElement: React.Dispatch<React.SetStateAction<SVGSVGElement | HTMLElement | null>>,
      onOpenFilter: OpenFilterHandler,
    ) => {
      return useCallback(
        (event: React.MouseEvent<SVGSVGElement | HTMLElement> | React.KeyboardEvent<SVGSVGElement | HTMLElement>) => {
          onOpenFilter(event, anchorElement, setAnchorElement);
        },
        [anchorElement, setAnchorElement, onOpenFilter],
      );
    };

    const hasPermissionToManageActions = examinerMode
      ? true
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const hasPermissionForGroupTaskActions = examinerMode
      ? examinerFunction === ExaminerFunction.KK
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const hasPermissionForExamTaskGroupCoordinatorActions = examinerMode
      ? [ExaminerFunction.KK, ExaminerFunction.KGZ].includes(examinerFunction)
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const { columns: treemapColumns } = useExaminerTreemapTableColumns();

    const clearRefs = useCallback(() => {
      selectedAssessmentSessionId.current = '';
      selectedItemId.current = '';
      selectedTaskGroup.current = undefined;
    }, []);

    useEffect(() => {
      if (props.data) {
        setExaminers(props.data);
        clearRefs();
      }
    }, [props.data, clearRefs]);

    const closeMenu = useCallback(() => {
      setMenuAnchor(undefined);
    }, []);

    useEffect(() => {
      setMenuAnchor(undefined);
    }, [props.isTableScrolled]);

    const handleReload = useCallback(() => {
      selectedItemId.current = '';
      closeMenu();
      reload();
    }, [reload]);

    const {
      allTreemapItemsCount,
      treemapData,
      action,
      isAddSampleAssessmentGroupConfirmationVisible,
      openAddKGZPrompt,
      renderStructureItemsCount,
      setIsAddSampleAssessmentGroupConfirmationVisible,
      setOpenKGZPrompt,
      setRenderStructureItemsCount,
      selectedSampleAsssessmentGroup,
      removedExaminatorFromZeByTeamMemberIdRef,
    } = useExaminerTreemapTableRows({
      data: props.data,
      expandedItems,
      examinerFunction,
      handleExpandedItems,
      examinerMode,
      openAssignModal,
      selectedItemId,
      selectedTaskGroup,
      setIsConfirmationPromptVisible,
      setPromptMainText,
      setPromptSubTexts,
      hasPermissionForExamTaskGroupCoordinatorActions,
      hasPermissionForGroupTaskActions,
      selectedAssessmentSessionId,
      setEditExaminerNumberModalOpen,
      setIsAssignSampleAssessmentGroupChairmanDialogVisible,
      setMessageModal,
      closeMenu,
      handleReload,
      hasPermissionToManageActions,
      onModalFunctionAction,
      setIsErrorPromptVisible,
      setIsExaminingTeamChairmen,
      setOpenDisableExaminerPrompt,
      setOpenDisableExaminingTeamPrompt,
      setOpenEnableExaminerPrompt,
      setOpenLimitCardPerExaminerDialog,
      setOpenUnassignDialog,
      examiningTeamMember,
      selectedExaminer,
      selectedSecondaryId,
      isTechAdmin,
    });

    const handleClickTaskGroupMenu = useMenuHandler(anchorElTaskGroup, setAnchorElTaskGroup, onOpenFilter);
    const handleClickFunctionMenu = useMenuHandler(anchorElFunction, setAnchorElFunction, onOpenFilter);
    const handleClickExamCommitteeMenu = useMenuHandler(anchorElExamCommittee, setAnchorElExamCommittee, onOpenFilter);

    useImperativeHandle(ref, () => ({
      ReloadTreeMap() {
        closeMenu();
        reload();
      },
      CloseMenu() {
        closeMenu();
      },
      selectedItemId: selectedItemId.current,
      selectedSecondaryId: selectedSecondaryId.current,
    }));

    const {
      handleUpdateExaminerNumber,
      removeExaminignTeamChairmanAssignment,
      removeExaminingTeamChairmanFunction,
      removeSampleAssessmentChairmanAssignment,
      removeSampleAssessmentGroup,
      removeSampleAssessmentMemberAssignment,
      removeVerifierFunction,
    } = useExaminerTreemapApiActions({
      closeMenu,
      reload,
      removedExaminatorFromZeByTeamMemberIdRef,
      selectedItemId,
      selectedTaskGroup,
      setEditExaminerNumberModalOpen,
      setIsErrorPromptVisible,
      setIsSuccessPromptVisible,
      setPromptMainText,
      setPromptSubTexts,
    });

    useEffect(() => {
      if (!props.data) return;
      if (examiners?.length === 0) {
        setIsEmptyResult(true);
      } else setIsEmptyResult(false);
    }, [examiners, props.data]);

    useEffect(() => {
      if (!props.data) return;
      if (
        examiners?.length &&
        props.isAnyInputActive &&
        (props.checkedFunctionsMenuItems.length > 0 || props.checkedExamCommitteeMenuItems.length > 0)
      ) {
        setExaminersFound([]);
        const foundIds = findExaminersBasedOnFilter(
          examiners,
          props.checkedFunctionsMenuItems,
          props.checkedExamCommitteeMenuItems,
        );
        setExaminersFound(foundIds);
      }
    }, [examiners, props.checkedFunctionsMenuItems, props.data, props.checkedExamCommitteeMenuItems]);

    useEffect(() => {
      if (
        examinersFound.length === 0 &&
        props.isAnyInputActive &&
        (props.checkedFunctionsMenuItems.length > 0 || props.checkedExamCommitteeMenuItems.length > 0)
      ) {
        setExaminers([]);
      } else setExaminers(props.data);
    }, [examinersFound, props.checkedFunctionsMenuItems, props.checkedExamCommitteeMenuItems]);

    useEffect(() => {
      setExaminers(props.data);
    }, [props.checkedFunctionsMenuItems, props.checkedExamCommitteeMenuItems]);

    const closeFilterItem = useCallback(
      (item?: string) => {
        clearRefs();
        if (item) handleCloseFilterItem(item);
      },
      [handleCloseFilterItem],
    );

    const sessionDataForNewMessage: AssementSessionData = {
      id: props.assessmentSessionId,
      name: props.assessmentSessionName,
      sampleAssessmentGroupName: selectedTaskGroup.current ? selectedTaskGroup.current.name : '',
      assessmentSessionId: selectedAssessmentSessionId.current ? selectedAssessmentSessionId.current : '',
      examTaskGroupName: selectedTaskGroup.current ? selectedTaskGroup.current.name : '',
    };

    const handleFilterChange = useCallback((name: string, value: string | string[]) => {
      const singleValue = Array.isArray(value) ? value[0] : value;
      clearRefs();
      if (name === 'taskGroupName') {
        props.handleTaskGroupFilterChange(singleValue);
      } else if (name === 'function') {
        props.handleFunctionsFilterChange(singleValue);
      } else {
        props.handleSearchInput(name, singleValue);
      }
    }, []);

    const filterElements: { [key: string]: JSX.Element } = useMemo(
      () => ({
        taskGroupName: (
          <>
            <TooltipOne title="Ustaw filtr wyszukiwania: Grupa zadań" placement="top">
              <FilterListIcon
                className={
                  props.checkedTaskGroupsMenuItems.length > 0
                    ? [classes.filterListIcon, tableClasses.activeFilter].join(' ')
                    : classes.filterListIcon
                }
                style={{ paddingLeft: '15px' }}
                onClick={handleClickTaskGroupMenu}
                aria-controls="TaskGroup-menu"
                aria-haspopup="true"
                fill={fill}
              />
            </TooltipOne>
            <DropdownMenuFilter
              anchorEl={anchorElTaskGroup}
              setAnchorEl={setAnchorElTaskGroup}
              menuItems={props.filterTaskGroupsMenuItems}
              hasCheckbox={true}
              checkedItems={props.checkedTaskGroupsMenuItems}
              onChange={props.handleTaskGroupFilterChange}
              uncheckAllMenuItems={props.uncheckAllTaskGroups}
            />
          </>
        ),
        function: (
          <>
            <TooltipOne title="Ustaw filtr wyszukiwania: Funkcja" placement="top">
              <FilterListIcon
                className={
                  props.checkedFunctionsMenuItems.length > 0
                    ? [classes.filterListIcon, tableClasses.activeFilter].join(' ')
                    : classes.filterListIcon
                }
                onClick={handleClickFunctionMenu}
                aria-controls="TaskGroup-menu"
                aria-haspopup="true"
              />
            </TooltipOne>
            <DropdownMenuFilter
              anchorEl={anchorElFunction}
              setAnchorEl={setAnchorElFunction}
              menuItems={props.filterFunctionsMenuItems}
              hasCheckbox={true}
              checkedItems={props.checkedFunctionsMenuItems}
              onChange={props.handleFunctionsFilterChange}
              uncheckAllMenuItems={props.uncheckAllFunctions}
            />
            <DropdownMenuFilter
              anchorEl={anchorElFunction}
              setAnchorEl={setAnchorElFunction}
              menuItems={props.filterFunctionsMenuItems}
              hasCheckbox={true}
              checkedItems={props.checkedFunctionsMenuItems}
              onChange={props.handleFunctionsFilterChange}
              uncheckAllMenuItems={props.uncheckAllFunctions}
            />
          </>
        ),
        examCommittee: (
          <>
            <TooltipOne title="Ustaw filtr wyszukiwania: Komisja" placement="top">
              <FilterListIcon
                className={
                  props.checkedExamCommitteeMenuItems.length > 0
                    ? [classes.filterListIcon, tableClasses.activeFilter].join(' ')
                    : classes.filterListIcon
                }
                onClick={handleClickExamCommitteeMenu}
                aria-controls="ExamCommittee-menu"
                aria-haspopup="true"
              />
            </TooltipOne>
            <DropdownMenuFilter
              anchorEl={anchorElExamCommittee}
              setAnchorEl={setAnchorElExamCommittee}
              menuItemsWithValue={convertToDropdownFilterLabelItems()}
              hasCheckbox={true}
              checkedItems={props.checkedExamCommitteeMenuItems}
              onChange={props.handleExamCommitteeFilterChange}
              uncheckAllMenuItems={props.uncheckAllExamCommittee}
            />
          </>
        ),
      }),
      [
        classes.filterListIcon,
        anchorElTaskGroup,
        anchorElFunction,
        anchorElExamCommittee,
        props.filterTaskGroupsMenuItems,
        props.checkedTaskGroupsMenuItems,
        props.checkedExamCommitteeMenuItems,
        props.filterFunctionsMenuItems,
        props.checkedFunctionsMenuItems,
        props.handleTaskGroupFilterChange,
        props.handleFunctionsFilterChange,
        props.handleExamCommitteeFilterChange,
        props.uncheckAllTaskGroups,
        props.uncheckAllFunctions,
        props.uncheckAllExamCommittee,
        handleClickTaskGroupMenu,
        handleClickFunctionMenu,
        handleClickExamCommitteeMenu,
      ],
    );

    const filterbar = useMemo(
      () => (props.isAnyFiltersActive ? props.filterState : []),
      [props.filterState, props.isAnyFiltersActive],
    );

    if (!isSessionLoaded || !props.assessmentSessionId || treemapData === undefined) {
      return null;
    }

    return (
      <>
        <div
          className={[tableClasses.adminTableContainerFlex, tableClasses.adminTableContainerFullWidth].join(' ')}
          style={{
            width: '100%',
            padding: 0,
            paddingTop: '20px',
            maxHeight: 'calc(100vh - 260px)',
            opacity: props.isLoader ? 0 : 1,
          }}
        >
          <SelectableTable<ExaminerTreemapTableItem>
            columns={treemapColumns}
            rows={treemapData}
            onFilterChange={handleFilterChange}
            filter={defaultFilter}
            filterElements={filterElements}
            closeFilterItem={closeFilterItem}
            uncheckAllFilters={props.uncheckAllFilters}
            filterbar={filterbar}
            caption="Zarządzanie strukturą egzaminatorów"
            footer={
              allTreemapItemsCount.current > renderStructureItemsCount ? (
                <CircularProgress size={20} style={{ marginTop: '20px' }} />
              ) : undefined
            }
            onScrollTable={(endReached) => {
              if (endReached) {
                setRenderStructureItemsCount((prevValue) => {
                  const newValue =
                    prevValue < allTreemapItemsCount.current
                      ? prevValue + treemapItemsPagination
                      : Math.max(allTreemapItemsCount.current, treemapItemsPagination);
                  return newValue;
                });
              }
            }}
          />
        </div>

        {isEmptyResult && props.data.length === 0 && <NoRecords />}
        {isAddSampleAssessmentGroupConfirmationVisible && selectedTaskGroup.current && (
          <AddSampleAssessmentGroupDialog
            isAddSampleAssessmentGroupConfirmationVisible={isAddSampleAssessmentGroupConfirmationVisible}
            hideAddSampleAssessmentGroupConfirmation={() => setIsAddSampleAssessmentGroupConfirmationVisible(false)}
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setIsAddSampleAssessmentGroupConfirmationVisible(false);
            }}
            onReload={reload}
            onFailure={(isNewSampleGroupCreated: boolean) => {
              if (isNewSampleGroupCreated) {
                handleReload();
              }
              setIsAddSampleAssessmentGroupConfirmationVisible(false);
            }}
          />
        )}
        {selectedSampleAsssessmentGroup &&
          selectedTaskGroup.current &&
          isAssignSampleAssessmentGroupChairmantDialogVisible && (
            <SampleAssessmentGroupChairmanAssignDialog
              taskGroup={selectedTaskGroup.current}
              sampleAssessmentId={selectedSampleAsssessmentGroup}
              onSuccess={() => {
                handleReload();
                setIsAssignSampleAssessmentGroupChairmanDialogVisible(false);
              }}
              onFailure={() => {
                setIsAssignSampleAssessmentGroupChairmanDialogVisible(false);
              }}
            />
          )}
        {selectedExaminer.current && openDisableExaminerPrompt && (
          <DisableExaminer
            onSuccess={() => {
              handleReload();
              setOpenDisableExaminerPrompt(false);
            }}
            onFailure={() => {
              setOpenDisableExaminerPrompt(false);
            }}
            examiner={selectedExaminer.current}
            isExaminingTeamChairmen={isExaminingTeamChairmen}
            isVerifier={selectedExaminer.current.examinerPermission[0]?.isVerifier ?? false}
          />
        )}
        {selectedSecondaryId.current && openDisableExaminingTeamPrompt && (
          <DisableExaminingTeam
            onSuccess={() => {
              handleReload();
              setOpenDisableExaminingTeamPrompt(false);
            }}
            onFailure={() => {
              setOpenDisableExaminingTeamPrompt(false);
            }}
            examiningTeamId={selectedSecondaryId.current}
          />
        )}
        {selectedExaminer.current && openEnableExaminerPrompt && (
          <EnableExaminer
            onSuccess={() => {
              handleReload();
              setOpenEnableExaminerPrompt(false);
            }}
            onFailure={() => {
              setOpenEnableExaminerPrompt(false);
            }}
            examiner={selectedExaminer.current}
            isExaminingTeamChairmen={isExaminingTeamChairmen}
            lockType={selectedExaminer.current.examinerPermission[0]?.isBlocked ?? BlockadeTypeId.Manual}
            isControlProcessActive={props.isControlProcessActive}
            examinerPermissionId={''}
            assessmentSessionId={props.assessmentSessionId}
            isVerifier={selectedExaminer.current.examinerPermission[0]?.isVerifier ?? false}
          />
        )}
        {selectedTaskGroup.current && openAddKGZPrompt && (
          <AddKGZPrompt
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setOpenKGZPrompt(false);
            }}
            onFailure={() => {
              setOpenKGZPrompt(false);
            }}
          />
        )}

        {selectedExaminer.current && selectedTaskGroup.current && openUnassignDialog && (
          <UnassignExamTaskGroupCoordinatorDialog
            itemId={selectedItemId.current}
            examiner={selectedExaminer.current}
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setOpenUnassignDialog(false);
            }}
            onFailure={() => {
              setOpenUnassignDialog(false);
            }}
          />
        )}
        {openLimitCardPerExaminerDialog &&
          selectedExaminer.current &&
          selectedTaskGroup.current &&
          selectedSecondaryId.current && (
            <SessionsParametersCardLimitPerExaminerDialog
              selectedExaminer={selectedExaminer.current}
              selectedTaskGroup={selectedTaskGroup.current}
              examiningTeamId={selectedSecondaryId.current}
              onClose={() => setOpenLimitCardPerExaminerDialog(false)}
              onOpen={openLimitCardPerExaminerDialog}
            />
          )}
        <Prompt
          type="confirmation"
          isBold={true}
          open={isConfirmationPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => {
            setIsConfirmationPromptVisible(false);
            switch (action) {
              case 'Usuń funkcję PZE':
                removeExaminingTeamChairmanFunction();
                break;
              case 'Usuń przewodniczącego z ZWO':
                removeSampleAssessmentChairmanAssignment();
                break;
              case 'Usuń członka ZWO':
                removeSampleAssessmentMemberAssignment();
                break;
              case 'Usuń egzaminatorowi funkcję PZE':
                removeExaminignTeamChairmanAssignment();
                break;
              case 'Usuń ZWO':
                removeSampleAssessmentGroup();
                break;
              case 'Usuń egzaminatorowi funkcję Egzaminator-Weryfikator':
                removeVerifierFunction();
                break;
            }
          }}
          onCancel={() => setIsConfirmationPromptVisible(false)}
          mainActionButtonText="TAK"
        />
        <Prompt
          type="success"
          isBold={true}
          open={isSuccessPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => {
            handleReload();
            setIsSuccessPromptVisible(false);
          }}
          mainActionButtonText="POWRÓT DO LISTY"
        />
        <Prompt
          type="error"
          isBold={true}
          open={isErrorPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => setIsErrorPromptVisible(false)}
          mainActionButtonText="POWRÓT DO LISTY"
        />
        <NewMessageModal
          open={messageModal.open}
          type={messageModal.type}
          onClose={() => {
            selectedExaminer.current = undefined;
            setMessageModal({ open: false, type: 'standard' });
          }}
          onSend={() => setMessageModal({ open: false, type: 'standard' })}
          recipientWithFunctionIdFromExaminerTreemap={examiningTeamMember.current}
          session={sessionDataForNewMessage}
        ></NewMessageModal>
        {selectedExaminer.current && (
          <ExaminerAssignNumber
            open={editExaminerNumberModalOpen}
            isBold={true}
            closeDialogs={() => setEditExaminerNumberModalOpen(false)}
            confirm={handleUpdateExaminerNumber}
            cancel={() => setEditExaminerNumberModalOpen(false)}
            isEdit={true}
            examiners={[
              {
                firstName: selectedExaminer.current.firstName,
                lastName: selectedExaminer.current.lastName,
                userId: selectedExaminer.current.examinerId,
                examinerNumber: selectedExaminer.current.examinerPermission[0]?.examinerNumber ?? '',
                id: selectedExaminer.current.examinerPermission[0]?.examinerPermissionId ?? '',
              },
            ]}
          />
        )}
      </>
    );
  },
);
export default ExaminerTreemapTable;
ExaminerTreemapTable.displayName = 'ExaminerTreemapTable';
